import React, { useEffect, useState } from "react";
import { Quote, PenLine, Star, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/i18n/SiteContext";
import { api, errMsg, tr } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { Stars } from "@/components/site/Stars";
import { Label, inputCls } from "@/components/site/QuickRequestForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const fmtDate = (d, lang) => {
  try { return new Date(d).toLocaleDateString(lang, { month: "long", year: "numeric" }); } catch { return d; }
};

const ReviewForm = ({ tours, onDone }) => {
  const { t, lang } = useSite();
  const [f, setF] = useState({ name: "", country: "", rating: 0, text: "", tour_slug: "any" });
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error(t("rev.chooseRating"));
    if (!f.name || f.text.trim().length < 10) return toast.error(t("err.required"));
    setLoading(true);
    try {
      await api.post("/reviews", { ...f, tour_slug: f.tour_slug === "any" ? "" : f.tour_slug, lang });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, t("err.generic")));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="review-success">
        <CheckCircle2 size={44} className="mx-auto text-sage-dark" />
        <h3 className="font-serif text-2xl text-ocean mt-4">{t("rev.thanksTitle")}</h3>
        <p className="mt-2 text-sm text-ocean/65">{t("rev.thanksText")}</p>
        <button onClick={onDone} className="mt-6 px-6 py-3 rounded-full bg-ocean text-ivory text-xs tracking-[0.18em] uppercase btn-lift" data-testid="review-success-close">{t("rev.close")}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="review-form">
      <div>
        <Label>{t("rev.rating")} *</Label>
        <div className="flex gap-1" onMouseLeave={() => setHover(0)} role="radiogroup" aria-label={t("rev.rating")}>
          {[1, 2, 3, 4, 5].map((i) => (
            <button type="button" key={i} role="radio" aria-checked={f.rating === i} aria-label={`${i}`} data-testid={`review-star-${i}`}
              onMouseEnter={() => setHover(i)} onClick={() => setF({ ...f, rating: i })}
              className="p-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sea">
              <Star size={28} strokeWidth={1.4} className={(hover || f.rating) >= i ? "text-ocean fill-ocean" : "text-ocean/25"} style={{ transition: "color .15s ease" }} />
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>{t("rev.name")} *</Label><input required className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-name-input" /></div>
        <div><Label>{t("rev.country")}</Label><input className={inputCls} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="review-country-input" /></div>
      </div>
      <div>
        <Label>{t("rev.tour")}</Label>
        <Select value={f.tour_slug} onValueChange={(v) => setF({ ...f, tour_slug: v })}>
          <SelectTrigger className="h-12 rounded-xl border-sand bg-white text-ocean" data-testid="review-tour-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            <SelectItem value="any">{t("rev.anyTour")}</SelectItem>
            {tours.map((x) => <SelectItem key={x.slug} value={x.slug}>{tr(x, lang).title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("rev.text")} *</Label>
        <textarea required rows={4} minLength={10} className={`${inputCls} h-auto py-3 resize-none`} placeholder={t("rev.textPh")} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="review-text-input" />
      </div>
      <button type="submit" disabled={loading} className="w-full px-6 py-4 rounded-full bg-ocean text-ivory text-xs font-medium tracking-[0.2em] uppercase hover:bg-ocean-700 disabled:opacity-60 btn-lift" data-testid="review-submit-button">
        {loading ? t("rev.sending") : t("rev.submit")}
      </button>
    </form>
  );
};

const PlatformBadge = ({ name, url, rating, count, id }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`reviews-platform-${id}`}
    className="flex items-center justify-between gap-4 px-5 py-4 rounded-2xl bg-white border border-sand/70 hover:border-sea shadow-soft btn-lift">
    <div>
      <p className="text-[11px] tracking-[0.2em] uppercase text-ocean/55">{name}</p>
      {rating > 0 ? (
        <p className="mt-1 flex items-center gap-2"><span className="font-serif text-xl text-ocean">{Number(rating).toFixed(1)}</span><Stars value={rating} size={13} />{count > 0 && <span className="text-xs text-ocean/50">({count})</span>}</p>
      ) : null}
    </div>
    <ExternalLink size={16} className="text-sea shrink-0" />
  </a>
);

export const ReviewsSection = ({ tours = [] }) => {
  const { t, lang, settings } = useSite();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/reviews", { params: { limit: 12 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], average: 0, count: 0 }));
  }, []);

  const titleOf = (slug) => {
    const x = tours.find((y) => y.slug === slug);
    return x ? tr(x, lang).title : "";
  };

  return (
    <section className="section-padding bg-white" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <p className="text-xs font-medium tracking-super-wide uppercase text-sea mb-4">{t("rev.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ocean leading-tight">{t("rev.title")}</h2>
          <p className="mt-5 text-base text-ocean/65 leading-relaxed">{t("rev.sub")}</p>
          <div className="brand-line mt-6" />
          {data && data.count > 0 && (
            <div className="mt-8 flex items-end gap-4" data-testid="reviews-summary">
              <span className="font-serif text-6xl text-ocean leading-none" data-testid="reviews-average">{Number(data.average).toFixed(1)}</span>
              <div className="pb-1">
                <Stars value={data.average} size={18} testId="reviews-average-stars" />
                <p className="mt-1 text-xs text-ocean/55">{t("rev.basedOn", { n: data.count })}</p>
              </div>
            </div>
          )}
          <div className="mt-8 space-y-3">
            {settings.google_reviews_url && <PlatformBadge id="google" name={t("rev.readOn", { src: "Google" })} url={settings.google_reviews_url} rating={settings.google_rating} count={settings.google_count} />}
            {settings.tripadvisor_url && <PlatformBadge id="tripadvisor" name={t("rev.readOn", { src: "Tripadvisor" })} url={settings.tripadvisor_url} rating={settings.tripadvisor_rating} count={settings.tripadvisor_count} />}
          </div>
          <button onClick={() => setOpen(true)} data-testid="reviews-write-button"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-ocean text-ocean text-xs font-medium tracking-[0.2em] uppercase hover:bg-ocean hover:text-ivory btn-lift">
            <PenLine size={15} /> {t("rev.write")}
          </button>
        </Reveal>

        <div className="lg:col-span-8 min-w-0">
          {!data ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{[0, 1].map((i) => <div key={i} className="h-64 rounded-3xl bg-sand/50 animate-pulse" />)}</div>
          ) : data.items.length === 0 ? (
            <div className="h-full min-h-[240px] rounded-3xl border border-dashed border-sand flex items-center justify-center text-ocean/55 text-sm p-8 text-center" data-testid="reviews-empty">{t("rev.empty")}</div>
          ) : (
            <Carousel opts={{ align: "start", loop: data.items.length > 2 }} className="w-full" data-testid="reviews-carousel">
              <CarouselContent className="-ml-5">
                {data.items.map((r) => (
                  <CarouselItem key={r.id} className="pl-5 md:basis-1/2">
                    <article className="h-full flex flex-col bg-ivory rounded-3xl p-8 border border-sand/60" data-testid={`review-card-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <Stars value={r.rating} size={15} />
                        <Quote size={26} className="text-sand-brand" />
                      </div>
                      <p className="mt-5 font-serif text-lg md:text-xl text-ocean leading-relaxed flex-1">“{r.text}”</p>
                      <div className="mt-6 pt-5 border-t border-sand/70 flex items-end justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ocean">{r.name}{r.country ? <span className="font-normal text-ocean/55"> · {r.country}</span> : null}</p>
                          {titleOf(r.tour_slug) && <p className="text-xs text-ocean/50 mt-1 truncate">{titleOf(r.tour_slug)}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px] tracking-[0.2em] uppercase text-sea">{t(`rev.source.${r.source}`)}</p>
                          <p className="text-[11px] text-ocean/45 mt-0.5">{fmtDate(r.date, lang)}</p>
                        </div>
                      </div>
                    </article>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="mt-8 flex gap-3 justify-end">
                <CarouselPrevious className="static translate-y-0 w-11 h-11 border-ocean/30 text-ocean hover:bg-ocean hover:text-ivory" data-testid="reviews-prev" />
                <CarouselNext className="static translate-y-0 w-11 h-11 border-ocean/30 text-ocean hover:bg-ocean hover:text-ivory" data-testid="reviews-next" />
              </div>
            </Carousel>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg rounded-3xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ocean">{t("rev.formTitle")}</DialogTitle>
            <DialogDescription className="text-ocean/60">{t("rev.formSub")}</DialogDescription>
          </DialogHeader>
          {open && <ReviewForm tours={tours} onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
